'use client';
import { useLocale, useTranslations } from 'next-intl';

import { Grid } from '@hrworks/sui-core/Grid';
import { GridItem } from '@hrworks/sui-core/GridItem';
import { Text } from '@hrworks/sui-core/Text';

import { useIsCustomDomain } from '../../context/CustomDomainContext';
import { resolveRoute } from '../../util/routeResolver';
import { Card, CardBody } from '../Card';
import { Link } from '../Link';
import { LinkButton } from '../LinkButton';
import { SocialIcons } from '../SocialIcons';
import type { SidebarProps } from './Sidebar.types';
import { SidebarCompanyDescriptions } from './SidebarCompanyDescriptions';
import { SidebarContact } from './SidebarContact';
import { SidebarEstablishments } from './SidebarEstablishments';
import { SidebarSection } from './SidebarSection';

export const Sidebar = ({
  post,
  companyId,
  settings,
  hideCompanyDescriptions,
  hideApplyButton,
  ...otherProps
}: SidebarProps) => {
  const t = useTranslations();
  const locale = useLocale();
  const isCustom = useIsCustomDomain();
  const { postOffer, contactInformation, scopeOfActivitiesKey } = post;
  const {
    careerLevelKey,
    workingTimeModelKey,
    employmentContractKey,
    permanentEstablishments,
    placeOfWorkTypeKey,
    id,
  } = postOffer;

  // Remote/Hybrid zeigt die Arbeitsform als zusätzlichen Standort – dann braucht es die
  // Standorte-Sektion auch ohne gepflegte Adressen.
  const hasEstablishmentsSection =
    (permanentEstablishments && permanentEstablishments.length > 0) ||
    (placeOfWorkTypeKey && placeOfWorkTypeKey !== 'onsite');

  const hasDetailSections =
    (!!scopeOfActivitiesKey && scopeOfActivitiesKey !== 'notSpecified') ||
    (!!careerLevelKey && careerLevelKey !== 'notSpecified') ||
    (!!workingTimeModelKey && workingTimeModelKey !== 'notSpecified') ||
    (!!employmentContractKey && employmentContractKey !== 'notSpecified') ||
    !!hasEstablishmentsSection;

  // Wie im Legacy-Portal: die Firmen-Profile am Ende der Detail-Card (nicht in der Kontakt-Kachel).
  const hasSocialProfiles = Object.values(settings.profiles ?? {}).some(Boolean);

  const jobListBase = resolveRoute('jobList', { locale, companyId: isCustom ? undefined : companyId });

  const buildFilterLink = (filterKey: string, filterValue: string) => {
    const separator = jobListBase.includes('?') ? '&' : '?';
    return `${jobListBase}${separator}${filterKey}=${encodeURIComponent(filterValue)}`;
  };

  const hideSidebarAndApplyButton = hideApplyButton && !hasDetailSections && !contactInformation;

  return (
    <>
      {!hideSidebarAndApplyButton && (
        <>
          {!hideApplyButton && (
            <LinkButton
              href={resolveRoute('jobApplication', {
                locale,
                companyId: isCustom ? undefined : companyId,
                id: id || '',
              })}
              data-guide-id="sidebar-apply-btn"
              width="full"
              size="large"
            >
              {t('Main.apply')}
            </LinkButton>
          )}
          {(hasDetailSections || hasSocialProfiles) && (
            <Card {...otherProps}>
              <CardBody>
                <Grid>
                  {scopeOfActivitiesKey && scopeOfActivitiesKey !== 'notSpecified' && (
                    <SidebarSection data-guide-id="sidebar-scope" title={t('Job.scope-of-activities')}>
                      <Link href={buildFilterLink('discipline', scopeOfActivitiesKey)}>
                        <Text>{t(`Job.scopeOfActivitiesKey.${scopeOfActivitiesKey}`)}</Text>
                      </Link>
                    </SidebarSection>
                  )}
                  {careerLevelKey && careerLevelKey !== 'notSpecified' && (
                    <SidebarSection data-guide-id="sidebar-career-level" title={t('Job.career-level')}>
                      <Link href={buildFilterLink('level', careerLevelKey)}>
                        <Text>{t(`Job.careerLevelKey.${careerLevelKey}`)}</Text>
                      </Link>
                    </SidebarSection>
                  )}
                  {workingTimeModelKey && workingTimeModelKey !== 'notSpecified' && (
                    <SidebarSection data-guide-id="sidebar-working-time" title={t('Job.working-time-model')}>
                      <Link href={buildFilterLink('time', workingTimeModelKey)}>
                        <Text>{t(`Job.workingTimeModelKey.${workingTimeModelKey}`)}</Text>
                      </Link>
                    </SidebarSection>
                  )}
                  {employmentContractKey && employmentContractKey !== 'notSpecified' && (
                    <SidebarSection data-guide-id="sidebar-contract-type" title={t('Job.employmentContractType')}>
                      <Link href={buildFilterLink('type', employmentContractKey)}>
                        <Text>{t(`Job.employmentContractTypeKey.${employmentContractKey}`)}</Text>
                      </Link>
                    </SidebarSection>
                  )}
                  {hasEstablishmentsSection && (
                    <SidebarEstablishments
                      data-guide-id="sidebar-locations"
                      permanentEstablishments={permanentEstablishments}
                      placeOfWorkTypeKey={placeOfWorkTypeKey}
                      remoteWorkName={settings.remoteWorkName}
                    />
                  )}
                  {hasSocialProfiles && (
                    <GridItem>
                      <SocialIcons profiles={settings.profiles} guideIdPrefix="sidebar" />
                    </GridItem>
                  )}
                </Grid>
              </CardBody>
            </Card>
          )}
          <SidebarContact contactInformation={contactInformation} />
        </>
      )}
      {!hideCompanyDescriptions && (
        <SidebarCompanyDescriptions
          data-guide-id="sidebar-company-description"
          companyDescriptions={settings.jobPortalCompanyDescriptions}
        />
      )}
    </>
  );
};
